// Main control panel layout for authenticated users
// Requirements: 5.1, 5.2

import React, { useState, useEffect } from 'react';
import { SidebarNavigation } from './SidebarNavigation';
import { AuthenticatedHeader } from './AuthenticatedHeader';
import { FeatureRouter } from './FeatureRouter';
import { useAuth } from '../../contexts/AuthContext';
import AIAssistant from '../common/AIAssistant';
import './ControlPanel.css';

export type FeatureModule = 'dashboard' | 'network' | 'inventory' | 'optimize' | 'planning';

interface ControlPanelProps {
  onLogout: () => void;
  onNavigateHome?: () => void;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({ onLogout, onNavigateHome }) => {
  const { state: authState } = useAuth();
  const [activeModule, setActiveModule] = useState<FeatureModule>('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAIAssistantVisible, setIsAIAssistantVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-collapse sidebar on mobile
      if (window.innerWidth < 768) {
        setIsSidebarCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Make active module available globally for AI Assistant context
  useEffect(() => {
    (window as any).currentActiveView = activeModule;
  }, [activeModule]);

  const handleModuleChange = (module: FeatureModule) => {
    setActiveModule(module);
    // Auto-collapse sidebar on mobile after navigation
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleAIAssistant = () => {
    setIsAIAssistantVisible(!isAIAssistantVisible);
  };

  if (!authState.user) {
    return (
      <div className="control-panel__error">
        <p>Authentication required. Please log in to access the control panel.</p>
      </div>
    );
  }

  return (
    <div className={`control-panel ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${isAIAssistantVisible ? 'ai-assistant-open' : ''}`}>
      {/* Authenticated Header */}
      <AuthenticatedHeader
        user={authState.user}
        onLogout={onLogout}
        onToggleSidebar={toggleSidebar}
        onToggleAIAssistant={toggleAIAssistant}
        isAIAssistantVisible={isAIAssistantVisible}
        isSidebarCollapsed={isSidebarCollapsed}
        onNavigateHome={onNavigateHome}
      />

      {/* Main Layout */}
      <div className="control-panel__layout">
        {/* Sidebar Navigation */}
        <SidebarNavigation
          activeModule={activeModule}
          onModuleChange={handleModuleChange}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          user={authState.user}
        />

        {/* Main Content Area */}
        <main className="control-panel__main" role="main">
          <div className="control-panel__content">
            <FeatureRouter
              activeModule={activeModule}
              onModuleChange={handleModuleChange}
            />
          </div>
        </main>

        {/* Mobile Sidebar Backdrop */}
        {isMobile && !isSidebarCollapsed && (
          <div
            className="control-panel__backdrop"
            onClick={() => setIsSidebarCollapsed(true)}
            aria-hidden="true"
          />
        )}
      </div>

      {/* AI Assistant Panel - Only available after login */}
      <AIAssistant 
        isVisible={isAIAssistantVisible}
        onToggle={toggleAIAssistant}
      />
    </div>
  );
};

export default ControlPanel;